import type { TFunction } from "i18next";

/**
 * One phone rule for the whole dashboard.
 *
 * Lenient on purpose: stores aren't Nepal-only (USD stores exist, and the
 * backend's own example is "+1-555-010-1234"), so a Nepal-mobile pattern
 * would reject real numbers. Digits plus the usual separators — spaces,
 * dashes, parentheses, a leading + — and 7 to 15 actual digits (ITU-T E.164).
 *
 * It existed three times: privately in the Upaya connect form, as a second
 * copy in onboarding's business-info step, and nowhere at all on Store
 * Settings, where "12ab" saved as the phone customers see.
 */
export const PHONE_ALLOWED_RE = /^[\d\s\-+()]+$/;
export const PHONE_MIN_DIGITS = 7;
export const PHONE_MAX_DIGITS = 15;

export type PhoneProblem = "invalidChars" | "tooShort" | "tooLong";

/** What's wrong with a phone number, or `null` when it's fine (or empty). */
export function phoneProblem(raw: string | null | undefined): PhoneProblem | null {
  const value = (raw ?? "").trim();
  if (!value) return null; // required-ness is a separate rule
  if (!PHONE_ALLOWED_RE.test(value)) return "invalidChars";
  const digits = value.replace(/\D/g, "").length;
  if (digits < PHONE_MIN_DIGITS) return "tooShort";
  if (digits > PHONE_MAX_DIGITS) return "tooLong";
  return null;
}

/**
 * An AntD `Form.Item` rule for a phone field. Empty passes — pair it with a
 * `required` rule where the field is required.
 */
export function phoneRule(t: TFunction) {
  const messages: Record<PhoneProblem, string> = {
    invalidChars: t("validation.phoneDigitsOnly", "Use digits only — spaces, dashes, parens, and + are allowed"),
    tooShort: t("validation.phoneTooShort", "Phone number is too short"),
    tooLong: t("validation.phoneTooLong", "Phone number is too long"),
  };
  return {
    validator: (_: unknown, value: string | undefined) => {
      const problem = phoneProblem(value);
      return problem ? Promise.reject(new Error(messages[problem])) : Promise.resolve();
    },
  };
}
